import React, { useEffect, useState } from 'react';
import { searchCustomers, searchOwners } from '../api';

// Debounced remote lookup. Each call is a login/logout against Acumatica, so we wait
// for typing to settle rather than firing per keystroke.
function useLookup(query, fetcher, minChars = 2) {
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!query || query.length < minChars) { setMatches([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      fetcher(query)
        .then((rows) => { if (!cancelled) { setMatches(rows.slice(0, 8)); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e.message); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return { matches, searching, error, clear: () => setMatches([]) };
}

// Everything that isn't the task tree: which customer, who manages it, when it starts.
// The customer must resolve to a real Acumatica CustomerID — the workbook only gives
// a name, and the create call refuses to run without the code.

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-gray-400">{hint}</span>}
    </label>
  );
}

const inputClass =
  'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm ' +
  'focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

export default function ProjectSettings({ quote, options, onChange }) {
  const [query, setQuery] = useState(quote.customer || '');
  const [ownerQuery, setOwnerQuery] = useState('');
  const [managerQuery, setManagerQuery] = useState('');
  const customers = useLookup(query, searchCustomers);
  const owners = useLookup(ownerQuery, searchOwners);
  const managers = useLookup(managerQuery, searchOwners);

  const selected = options.customerId;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-gray-900">Project settings</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Customer" hint={`Workbook says "${quote.customer || 'unknown'}" — pick the matching Acumatica customer`}>
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); onChange({ ...options, customerId: null }); }}
              placeholder="Search customers…"
              className={inputClass}
            />
          </Field>

          {selected ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-green-700">
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-green-200">
                {selected}
              </span>
              selected
            </p>
          ) : (
            <div className="mt-2 space-y-1">
              {customers.searching && <p className="text-sm text-gray-400">Searching…</p>}
              {customers.error && <p className="text-sm text-red-600">{customers.error}</p>}
              {customers.matches.map((c) => (
                <button
                  key={c.customerId}
                  type="button"
                  onClick={() => { onChange({ ...options, customerId: c.customerId }); setQuery(c.name); }}
                  className="block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                >
                  <code className="mr-2 font-mono text-xs text-gray-500">{c.customerId}</code>
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2">
          <Field
            label="Activity owner"
            hint="Every created activity is assigned to this employee — Acumatica rejects an activity with no owner"
          >
            <input
              value={ownerQuery}
              onChange={(e) => { setOwnerQuery(e.target.value); onChange({ ...options, owner: null }); }}
              placeholder="Search employees…"
              className={inputClass}
            />
          </Field>

          {options.owner ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-green-700">
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-green-200">
                {options.owner}
              </span>
              selected
            </p>
          ) : (
            <div className="mt-2 space-y-1">
              {owners.searching && <p className="text-sm text-gray-400">Searching…</p>}
              {owners.error && <p className="text-sm text-red-600">{owners.error}</p>}
              {owners.matches.map((o) => (
                <button
                  key={o.owner}
                  type="button"
                  onClick={() => { onChange({ ...options, owner: o.owner }); setOwnerQuery(o.name); }}
                  className="block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                >
                  <code className="mr-2 font-mono text-xs text-gray-500">{o.employeeId}</code>
                  {o.name}
                  {o.status !== 'Active' && <span className="ml-2 text-xs text-gray-400">({o.status})</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <Field label="Project manager" hint="Select a validated Acumatica employee">
            <input
              value={managerQuery}
              onChange={(e) => {
                setManagerQuery(e.target.value);
                onChange({ ...options, projectManager: null });
              }}
              placeholder="Search employees…"
              className={inputClass}
            />
          </Field>
          {options.projectManager ? (
            <p className="mt-2 text-sm text-green-700">
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-green-200">
                {options.projectManager}
              </span>{' '}selected
            </p>
          ) : (
            <div className="mt-2 space-y-1">
              {managers.searching && <p className="text-sm text-gray-400">Searching…</p>}
              {managers.error && <p className="text-sm text-red-600">{managers.error}</p>}
              {managers.matches.map((manager) => (
                <button
                  key={manager.owner}
                  type="button"
                  onClick={() => {
                    onChange({ ...options, projectManager: manager.employeeId });
                    setManagerQuery(manager.name);
                  }}
                  className="block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                >
                  <code className="mr-2 font-mono text-xs text-gray-500">{manager.employeeId}</code>
                  {manager.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <Field label="Start date" hint="Every activity is created on this date">
          <input
            type="date"
            value={options.startDate || ''}
            onChange={(e) => onChange({ ...options, startDate: e.target.value })}
            className={inputClass}
          />
        </Field>

        <Field label="Template" hint="Creates the 8 Finance tasks automatically">
          <input
            value={options.templateId || ''}
            onChange={(e) => onChange({ ...options, templateId: e.target.value })}
            className={inputClass}
          />
        </Field>

        <Field label="Description" hint="Leave blank to use the standard convention">
          <input
            value={options.description || ''}
            onChange={(e) => onChange({ ...options, description: e.target.value })}
            placeholder={`${quote.customer} - MYOB Acumatica Business Implementation`}
            className={inputClass}
          />
        </Field>
      </div>
    </div>
  );
}
